/**
 * 동기화 항목 목록에서 프로젝트/설비 이름을 일괄 해석하는 훅
 * 타임라인 뷰용 (그룹 없이 flat list에서 사용)
 */

import { useEffect, useState } from "react";
import type { SyncQueueItem } from "@/lib/db/types";
import { getCachedProject } from "@/lib/db/projects";
import { getCachedMachinesByProject } from "@/lib/db/machines";

export interface SyncItemNames {
  projectNames: Map<string, string>;
  machineNames: Map<string, string>;
}

const EMPTY: SyncItemNames = {
  projectNames: new Map(),
  machineNames: new Map(),
};

export function useSyncItemNames(items: SyncQueueItem[]): SyncItemNames {
  const [names, setNames] = useState<SyncItemNames>(EMPTY);

  useEffect(() => {
    if (items.length === 0) {
      setNames(EMPTY);
      return;
    }

    let cancelled = false;

    async function resolve() {
      const projectNames = new Map<string, string>();
      const machineNames = new Map<string, string>();

      const projectIds = new Set<string>();
      for (const item of items) {
        if (item.projectId) projectIds.add(item.projectId);
      }

      await Promise.all(
        [...projectIds].map(async (pid) => {
          const project = await getCachedProject(pid);
          if (project) {
            projectNames.set(pid, project.name);
          }

          const machines = await getCachedMachinesByProject(pid);
          for (const m of machines) {
            if (m.machine._id) machineNames.set(m.machine._id, m.machine.name);
            if (m.machine.machineId) machineNames.set(m.machine.machineId, m.machine.name);
          }
        })
      );

      if (!cancelled) {
        setNames({ projectNames, machineNames });
      }
    }

    resolve();
    return () => {
      cancelled = true;
    };
  }, [items]);

  return names;
}
